import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';

const ACCEPTED_TYPES = {
  'audio/wav': ['.wav'],
  'audio/mpeg': ['.mp3'],
  'audio/webm': ['.webm'],
  'audio/ogg': ['.ogg'],
  'audio/flac': ['.flac'],
  'audio/mp4': ['.m4a'],
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
};

const MAX_SIZE = 200 * 1024 * 1024; // 200 MB

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * FileUploader
 * Drag-and-drop + click-to-browse file selector for audio/video files.
 *
 * Props:
 *   onFileSelect {func} — called with (file) when a valid file is chosen
 *   onSubmit     {func} — called with (file) when user clicks Analyze
 *   isLoading    {bool} — disables controls while parent is uploading
 */
export default function FileUploader({ onFileSelect, onSubmit, isLoading }) {
  const [file, setFile] = useState(null);
  const [dropError, setDropError] = useState(null);

  const onDrop = useCallback((accepted, rejected) => {
    setDropError(null);

    if (rejected.length > 0) {
      const err = rejected[0].errors[0];
      if (err.code === 'file-too-large') {
        setDropError(`File is too large (${formatBytes(rejected[0].file.size)}). Maximum size is 200 MB.`);
      } else if (err.code === 'file-invalid-type') {
        setDropError('Unsupported file type. Use MP3, WAV, MP4, MOV, AVI, WebM, or OGG.');
      } else {
        setDropError(err.message);
      }
      return;
    }

    if (accepted.length > 0) {
      const selected = accepted[0];
      setFile(selected);
      onFileSelect?.(selected);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE,
    multiple: false,
    disabled: isLoading,
  });

  const handleSubmit = () => {
    if (file) onSubmit?.(file);
  };

  const handleClear = () => {
    setFile(null);
    setDropError(null);
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={
          isDragActive
            ? { borderColor: 'var(--accent)', background: 'var(--accent-dim)' }
            : file
            ? { borderColor: 'var(--color-success-border)', background: 'var(--color-success-dim)' }
            : { background: 'var(--bg-input)', borderColor: 'var(--border-subtle)' }
        }
        className={clsx(
          'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
          'hover:opacity-90',
          isLoading && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />

        {file ? (
          <div className="space-y-1">
            <div className="text-3xl">✅</div>
            <p className="font-medium truncate" style={{ color: 'var(--color-success)' }}>{file.name}</p>
            <p className="text-sm" style={{ color: 'var(--color-success)', opacity: 0.85 }}>
              {formatBytes(file.size)} · {file.type || 'audio/video'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-4xl">{isDragActive ? '📂' : '📁'}</div>
            <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>
              {isDragActive ? 'Drop your file here' : 'Drag & drop your audio or video'}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              or <span className="underline" style={{ color: 'var(--accent)' }}>click to browse</span>
            </p>
            <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
              MP3, WAV, MP4, MOV, AVI, WebM, OGG, FLAC · up to 200 MB
            </p>
          </div>
        )}
      </div>

      {/* Drop error */}
      {dropError && (
        <div className="px-4 py-3 rounded-lg text-sm" style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}>
          {dropError}
        </div>
      )}

      {/* Actions */}
      {file && (
        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            style={{ background: 'var(--accent)', color: 'var(--on-primary, #fff)' }}
            className="flex-1 py-3 rounded-lg font-medium hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Analyzing…' : '🔍 Analyze File'}
          </button>
          <button
            onClick={handleClear}
            disabled={isLoading}
            style={{ background: 'var(--bg-input)', border: '0.5px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
            className="px-4 py-3 rounded-lg hover:opacity-80 transition-opacity disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {/* Tips */}
      <div className="rounded-lg p-3 text-sm" style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)', color: 'var(--color-info)' }}>
        <strong>Tip:</strong> For best results, use a recording with minimal background noise.
        Sarvam AI supports 22+ Indian languages including Hindi, Tamil, and Telugu.
      </div>
    </div>
  );
}
